import React from 'react';
import { UrlPreview } from '../components/UrlPreview';

interface UrlMetadata {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
  image?: string;
}

// URL regex pattern
const URL_PATTERN = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;

export function useUrlPreviews() {
  const [urlMetadata, setUrlMetadata] = React.useState<Record<string, UrlMetadata>>({});

  const extractUrls = React.useCallback((text: string): string[] => {
    return text.match(URL_PATTERN) || [];
  }, []);

  const getFriendlyUrlText = React.useCallback((url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }, []);

  const fetchUrlMetadata = React.useCallback(async (url: string) => {
    if (urlMetadata[url]) return;

    try {
      const response = await fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}&screenshot=true`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setUrlMetadata(prev => ({
          ...prev,
          [url]: {
            url,
            title: data.data.title || getFriendlyUrlText(url),
            description: data.data.description,
            favicon: data.data.logo?.url || `https://www.google.com/s2/favicons?domain=${getFriendlyUrlText(url)}&sz=32`,
            image: data.data.image?.url || data.data.screenshot?.url
          }
        }));
      } else {
        setUrlMetadata(prev => ({
          ...prev,
          [url]: {
            url,
            title: getFriendlyUrlText(url),
            favicon: `https://www.google.com/s2/favicons?domain=${getFriendlyUrlText(url)}&sz=32`
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching URL metadata:', err);
      setUrlMetadata(prev => ({
        ...prev,
        [url]: {
          url,
          title: getFriendlyUrlText(url),
          favicon: `https://www.google.com/s2/favicons?domain=${getFriendlyUrlText(url)}&sz=32`
        }
      }));
    }
  }, [urlMetadata, getFriendlyUrlText]);

  const renderContent = React.useCallback((content: string, isEditing = false) => {
    if (!content) return null;

    const urls = extractUrls(content);
    if (urls.length === 0) {
      return isEditing ? null : React.createElement('p', { className: 'text-gray-900 whitespace-pre-wrap' }, content);
    }

    // When editing, only show URL previews
    if (isEditing) {
      return React.createElement('div', { className: 'w-full space-y-2' },
        urls.map((url, index) => 
          React.createElement(UrlPreview, {
            key: `preview-${index}`,
            url,
            metadata: urlMetadata[url] || { url, title: getFriendlyUrlText(url) }
          })
        )
      );
    }

    // For display, show both text and URL previews
    let lastIndex = 0;
    const elements: React.ReactNode[] = [];

    urls.forEach((url, index) => {
      const urlIndex = content.indexOf(url, lastIndex);
      
      if (urlIndex > lastIndex) {
        elements.push(
          React.createElement('span', { 
            key: `text-${index}`,
            className: 'whitespace-pre-wrap'
          }, content.slice(lastIndex, urlIndex))
        );
      }
      
      elements.push(
        React.createElement(UrlPreview, {
          key: `preview-${index}`,
          url,
          metadata: urlMetadata[url] || { url, title: getFriendlyUrlText(url) }
        })
      );
      
      lastIndex = urlIndex + url.length;
    });

    if (lastIndex < content.length) {
      elements.push(
        React.createElement('span', { 
          key: 'text-end',
          className: 'whitespace-pre-wrap'
        }, content.slice(lastIndex))
      );
    }

    return React.createElement('div', { className: 'w-full space-y-2' }, elements);
  }, [extractUrls, urlMetadata, getFriendlyUrlText]);

  return {
    urlMetadata,
    extractUrls,
    fetchUrlMetadata,
    renderContent
  };
}